import { useState } from "react";

import { Loader2, Sparkles } from "lucide-react";

import { PhotoUpload } from "@/components/fields/photo-upload";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";

interface TriggerParams {
  instagramScreenshot?: string;
}

interface ArtistEnrichmentTriggerProps {
  artistName: string;
  isSubmitting: boolean;
  onSubmit: (params: TriggerParams) => Promise<void>;
}

export function ArtistEnrichmentTrigger({
  artistName,
  isSubmitting,
  onSubmit,
}: ArtistEnrichmentTriggerProps) {
  const [instagramScreenshot, setInstagramScreenshot] = useState<string>("");

  const handleSubmit = () => {
    onSubmit({ instagramScreenshot: instagramScreenshot || undefined });
  };

  if (isSubmitting) {
    return (
      <div className="flex flex-col items-center justify-center py-32 gap-5 text-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
        <div>
          <p className="text-base font-medium">AI enrichment is running…</p>
          <p className="text-sm text-muted-foreground mt-1">
            This may take a minute or two. Please don't leave this page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Artist Information</CardTitle>
          <CardDescription>
            Current artist to be enriched with AI-powered insights
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm font-medium">{artistName}</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Instagram</CardTitle>
          <CardDescription>
            Upload an Instagram screenshot to pull bio and contact data for this
            artist
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label>Instagram Screenshot (optional)</Label>
            <PhotoUpload
              value={instagramScreenshot}
              onChange={(value) => setInstagramScreenshot(value ?? "")}
              transformToWebP
            />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-center pt-2">
        <Button size="lg" disabled={isSubmitting} onClick={handleSubmit}>
          <Sparkles className="h-4 w-4 mr-2" />
          Run AI Enrichment
        </Button>
      </div>
    </div>
  );
}
